import React, { useState, useEffect } from 'react';
import { Calculator, Calendar, ExternalLink, Info } from 'lucide-react';

// ---------- Hash deep-link helpers ----------
// URL format:
//   #knowledge-compliance-gst       → Knowledge Bank > Compliance > GST
//   #knowledge-compliance-income-tax → Knowledge Bank > Compliance > Income Tax
//   #knowledge-links                → Knowledge Bank > Useful Links
const VALID_SUBSECTIONS = ['calculators', 'compliance', 'links', 'rates'];
const VALID_CATEGORIES  = ['income-tax', 'tds', 'gst', 'roc', 'others'];

function readHash() {
  const raw = window.location.hash.replace(/^#/, '');
  if (!raw.startsWith('knowledge')) return null;
  const rest = raw.slice('knowledge'.length).replace(/^-/, '');   // e.g. "compliance-gst"
  const parts = rest.split('-');
  const subsection = parts[0] || 'calculators';
  if (!VALID_SUBSECTIONS.includes(subsection)) return null;
  if (subsection === 'compliance') {
    const cat = parts.slice(1).join('-');   // "income-tax", "gst", etc.
    return { subsection, category: VALID_CATEGORIES.includes(cat) ? cat : null };
  }
  return { subsection, category: null };
}

function writeHash(subsection, categoryId = null) {
  const hash = categoryId
    ? `knowledge-compliance-${categoryId}`
    : `knowledge-${subsection}`;
  // Use pushState so the URL changes and becomes copyable / bookmarkable
  window.history.pushState(null, '', `#${hash}`);
}
// -------------------------------------------

// Small button that copies the deep-link URL to clipboard and shows a tick for 2 s
const ShareLinkButton = ({ categoryId, categoryLabel }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    const url = `${window.location.origin}${window.location.pathname}#knowledge-compliance-${categoryId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Fallback for browsers that block clipboard
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      title={`Copy shareable link for ${categoryLabel}`}
      className="flex items-center gap-2 px-4 py-2 rounded-lg border font-dmsans text-sm font-semibold transition-all duration-200 whitespace-nowrap"
      style={{
        borderColor: copied ? '#22c55e' : '#C9A84C',
        color: copied ? '#22c55e' : '#C9A84C',
        backgroundColor: copied ? 'rgba(34,197,94,0.08)' : 'rgba(201,168,76,0.08)',
      }}
    >
      {copied ? (
        <>
          <span>✓</span>
          <span>Link Copied!</span>
        </>
      ) : (
        <>
          <span>🔗</span>
          <span>Copy Shareable Link</span>
        </>
      )}
    </button>
  );
};

const KnowledgeBankSection = () => {
  const [activeSubsection, setActiveSubsection] = useState('calculators');

  // GST Calculator State
  const [gstAmount, setGstAmount] = useState('');
  const [gstRate, setGstRate] = useState('18');
  const [gstResult, setGstResult] = useState(null);

  // EMI Calculator State
  const [principal, setPrincipal] = useState('');
  const [rate, setRate] = useState('');
  const [tenure, setTenure] = useState('');
  const [emiResult, setEmiResult] = useState(null);

  // Income Tax Calculator State
  const [income, setIncome] = useState('');
  const [taxRegime, setTaxRegime] = useState('new');
  const [taxResult, setTaxResult] = useState(null);

  const calculateGST = () => {
    const amount = parseFloat(gstAmount);
    const rate = parseFloat(gstRate);
    if (!isNaN(amount) && !isNaN(rate)) {
      const gst = (amount * rate) / 100;
      const total = amount + gst;
      setGstResult({ amount, gst, total });
    }
  };

  const calculateEMI = () => {
    const p = parseFloat(principal);
    const r = parseFloat(rate) / 12 / 100;
    const n = parseFloat(tenure) * 12;
    
    if (!isNaN(p) && !isNaN(r) && !isNaN(n) && r > 0 && n > 0) {
      const emi = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      const totalAmount = emi * n;
      const totalInterest = totalAmount - p;
      setEmiResult({ emi, totalAmount, totalInterest });
    }
  };

  const calculateIncomeTax = () => {
    const inc = parseFloat(income);
    if (isNaN(inc)) return;

    let tax = 0;
    
    if (taxRegime === 'old') {
      // Old Regime FY 2025-26 (with standard deduction)
      const taxableIncome = Math.max(0, inc - 50000); // Standard deduction
      if (taxableIncome <= 250000) tax = 0;
      else if (taxableIncome <= 500000) tax = (taxableIncome - 250000) * 0.05;
      else if (taxableIncome <= 1000000) {
        tax = 12500 + (taxableIncome - 500000) * 0.20;
      } else {
        tax = 12500 + 100000 + (taxableIncome - 1000000) * 0.30;
      }
    } else {
      // New Regime FY 2025-26
      if (inc <= 300000) tax = 0;
      else if (inc <= 700000) tax = (inc - 300000) * 0.05;
      else if (inc <= 1000000) {
        tax = 20000 + (inc - 700000) * 0.10;
      } else if (inc <= 1200000) {
        tax = 20000 + 30000 + (inc - 1000000) * 0.15;
      } else if (inc <= 1500000) {
        tax = 20000 + 30000 + 30000 + (inc - 1200000) * 0.20;
      } else {
        tax = 20000 + 30000 + 30000 + 60000 + (inc - 1500000) * 0.30;
      }
    }

    // Add 4% cess
    tax = tax * 1.04;
    const netIncome = inc - tax;
    setTaxResult({ tax, netIncome });
  };

  const subsections = [
    { id: 'calculators', label: 'Calculators', icon: Calculator },
    { id: 'compliance', label: 'Compliance Due Dates', icon: Calendar },
    { id: 'links', label: 'Useful Links', icon: ExternalLink },
    { id: 'rates', label: 'Key Reference Rates', icon: Info }
  ];

  const usefulLinks = [
    { name: 'Income Tax e-Filing Portal', url: 'https://incometax.gov.in' },
    { name: 'GST Portal', url: 'https://gst.gov.in' },
    { name: 'MCA21 Portal', url: 'https://mca.gov.in' },
    { name: 'ICAI Website', url: 'https://icai.org' },
    { name: 'RBI Website', url: 'https://rbi.org.in' },
    { name: 'SEBI Website', url: 'https://sebi.gov.in' },
    { name: 'EPF Member Portal', url: 'https://epfindia.gov.in' },
    { name: 'TRACES Portal', url: 'https://tdscpc.gov.in' }
  ];

  const [activeComplianceCategory, setActiveComplianceCategory] = useState(null);

  // ── On mount: read hash and restore state ──────────────────────────────────
  useEffect(() => {
    const parsed = readHash();
    if (parsed) {
      setActiveSubsection(parsed.subsection);
      if (parsed.subsection === 'compliance' && parsed.category) {
        setActiveComplianceCategory(parsed.category);
      }
      // Scroll the Knowledge Bank section into view smoothly
      setTimeout(() => {
        const el = document.getElementById('section-knowledge');
        if (el) {
          const offset = 90;
          window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - offset, behavior: 'smooth' });
        }
      }, 150);
    }
  }, []);

  // ── Listen for browser back / forward navigation ───────────────────────────
  useEffect(() => {
    const onPop = () => {
      const parsed = readHash();
      if (parsed) {
        setActiveSubsection(parsed.subsection);
        setActiveComplianceCategory(parsed.subsection === 'compliance' ? (parsed.category || null) : null);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // ── Helpers that update both state AND URL ─────────────────────────────────
  const navigateSubsection = (id) => {
    setActiveSubsection(id);
    setActiveComplianceCategory(null);
    writeHash(id);
  };

  const navigateCategory = (catId) => {
    setActiveComplianceCategory(catId);
    writeHash('compliance', catId);
  };

  const clearCategory = () => {
    setActiveComplianceCategory(null);
    writeHash('compliance');
  };

  const complianceCategories = [
    {
      id: 'income-tax',
      label: 'Income Tax',
      icon: '📋',
      color: '#1a6b3a',
      description: 'Key due dates for income tax filings, advance tax payments, and returns.',
      dates: [
        { task: 'Advance Tax – 1st Instalment (15% of tax liability)', due: '15th June', note: 'For all taxpayers except those under presumptive taxation' },
        { task: 'Advance Tax – 2nd Instalment (45% cumulative)', due: '15th September', note: '' },
        { task: 'Advance Tax – 3rd Instalment (75% cumulative)', due: '15th December', note: '' },
        { task: 'Advance Tax – 4th Instalment (100% cumulative)', due: '15th March', note: '' },
        { task: 'ITR Filing – Individuals / HUF (Non-Audit)', due: '31st July', note: 'FY 2024-25 returns' },
        { task: 'ITR Filing – Companies / Audit Cases', due: '31st October', note: 'Subject to tax audit requirement' },
        { task: 'ITR Filing – Transfer Pricing Cases', due: '30th November', note: 'Where international/specified domestic transactions exist' },
        { task: 'Tax Audit Report (Form 3CA/3CB & 3CD)', due: '30th September', note: 'For entities with turnover exceeding prescribed threshold' },
        { task: 'Form 10IE / 10IEA (Opt for New Regime)', due: 'Before filing ITR', note: 'Business income taxpayers only' },
        { task: 'Belated / Revised ITR', due: '31st December', note: 'For the relevant assessment year' },
      ]
    },
    {
      id: 'tds',
      label: 'TDS / TCS',
      icon: '💼',
      color: '#1B3A6B',
      description: 'TDS & TCS rates (IT Act 2025, Tax Year 2026-27), deposit deadlines, return filing due dates, and compliance notes.',
      alert: {
        type: 'warning',
        title: 'Structural Change w.e.f. 01 April 2026 — IT Act 2025',
        body: 'Income Tax Act, 2025 consolidates TDS under Section 392 (Salary) and Section 393 (All other TDS), and TCS under Section 394. TRACES now mandates 4-digit codes (1001–1092) in all quarterly returns. Old section numbers (194C, 194J, 206C etc.) cause portal validation errors for Tax Year 2026-27. Update Tally / SAP / Zoho before first filing. TDS certificates: Form 16 → Form 130 | Form 16A → Form 131 | TCS: Form 143',
      },
      sections: [
        {
          heading: 'TDS Deposit & Return Filing Due Dates',
          icon: '📅',
          dates: [
            { task: 'TDS Deposit (April to February deductions)', due: '7th of the following month', note: 'Via Challan 281N' },
            { task: 'TDS Deposit (March deductions – Government deductors)', due: '7th April', note: '' },
            { task: 'TDS Deposit (March deductions – Other deductors)', due: '30th April', note: '' },
            { task: 'TDS Return – Q1 (April–June) – Form 138 (Salary) / Form 139 (Non-Salary)', due: '31st July', note: 'Replaces 24Q / 26Q from TY 2026-27' },
            { task: 'TDS Return – Q2 (July–September) – Form 138 / Form 139', due: '31st October', note: '' },
            { task: 'TDS Return – Q3 (October–December) – Form 138 / Form 139', due: '31st January', note: '' },
            { task: 'TDS Return – Q4 (January–March) – Form 138 / Form 139', due: '31st May', note: '' },
            { task: 'TDS Certificate – Form 130 (Salary, replaces Form 16)', due: '15th June', note: 'Issued by employer after Q4 return' },
            { task: 'TDS Certificate – Form 131 (Non-Salary, replaces Form 16A)', due: '15 days from quarterly return due date', note: '' },
            { task: 'TCS Return – Form 143 (replaces Form 27EQ)', due: 'Same as TDS quarterly return dates', note: 'Q1: 15 Jul | Q2: 15 Oct | Q3: 15 Jan | Q4: 15 May' },
            { task: 'TCS Certificate – Form 133 (replaces Form 27D)', due: '15 days from TCS return due date', note: '' },
          ]
        },
        {
          heading: 'TDS Rate Chart — Section 393 (IT Act 2025) | Domestic Payments',
          icon: '📋',
          isRateTable: true,
          rates: [
            { nature: 'Salary — Tax at Source', newSec: '392(1)', code: '1001–03', oldSec: '192', rate: 'Slab', rateType: 'blue', threshold: 'As per applicable income tax slab' },
            { nature: 'EPF Accumulated Balance – Premature Withdrawal', newSec: '392(7)', code: '1004', oldSec: '192A', rate: '10%', rateType: 'gold', threshold: 'Premature withdrawal; conditions apply' },
            { nature: 'Commission / Brokerage – Insurance', newSec: '393(1) Sl.1(i)', code: '1005', oldSec: '194D', rate: '2% / 10%', rateType: 'gold', threshold: '₹20,000 | Individual: 2%; Others: 10%' },
            { nature: 'Commission / Brokerage – Others', newSec: '393(1) Sl.1(ii)', code: '1006', oldSec: '194H', rate: '2%', rateType: 'gold', threshold: '₹20,000 per year' },
            { nature: 'Rent – Plant & Machinery (Specified Person)', newSec: '393(1) Sl.2(ii).D(a)', code: '1008', oldSec: '194I(a)', rate: '2%', rateType: 'gold', threshold: '₹50,000 per month' },
            { nature: 'Rent – Land / Building (Specified Person)', newSec: '393(1) Sl.2(ii).D(b)', code: '1009', oldSec: '194I(b)', rate: '10%', rateType: 'gold', threshold: '₹50,000 per month' },
            { nature: 'JDA – Consideration under Sec. 67(14)', newSec: '393(1) Sl.3(ii)', code: '1011', oldSec: '194IC', rate: '10%', rateType: 'gold', threshold: 'No threshold' },
            { nature: 'Compensation – Acquisition of Immovable Property', newSec: '393(1) Sl.3(iii)', code: '1012', oldSec: '194IA', rate: '10%', rateType: 'gold', threshold: '₹5 Lakh' },
            { nature: 'Mutual Fund Units – Income to Resident', newSec: '393(1) Sl.4(i)', code: '1013', oldSec: '194K', rate: '10%', rateType: 'gold', threshold: '₹10,000' },
            { nature: 'Business Trust – Interest / Dividend (Resident)', newSec: '393(1) Sl.4(ii)', code: '1014/15', oldSec: '194LBA', rate: '10%', rateType: 'gold', threshold: 'No threshold' },
            { nature: 'Investment Fund Units – Income to Unitholder', newSec: '393(1) Sl.4(iii)', code: '1017', oldSec: '194LBB', rate: '10%', rateType: 'gold', threshold: 'No threshold' },
            { nature: 'Securitisation Trust – Income to Investor', newSec: '393(1) Sl.4(iv)', code: '1018', oldSec: '194LBC', rate: '10%', rateType: 'gold', threshold: 'No threshold' },
            { nature: 'Interest on Securities', newSec: '393(1) Sl.5(i)', code: '1019', oldSec: '193', rate: '10%', rateType: 'gold', threshold: '₹10,000' },
            { nature: 'Interest – Senior Citizen (Non-securities)', newSec: '393(1) Sl.5(ii).D(a)', code: '1020', oldSec: '194A', rate: '10%', rateType: 'gold', threshold: '₹1,00,000' },
            { nature: 'Interest – Others (Non-securities, Banks)', newSec: '393(1) Sl.5(ii).D(b)', code: '1021', oldSec: '194A', rate: '10%', rateType: 'gold', threshold: '₹50,000' },
            { nature: 'Interest – Others (Non-securities, Non-bank)', newSec: '393(1) Sl.5(iii)', code: '1022', oldSec: '194A', rate: '10%', rateType: 'gold', threshold: '₹10,000' },
            { nature: 'Contractor – Individual / HUF', newSec: '393(1) Sl.6(i).D(a)', code: '1023', oldSec: '194C', rate: '1% / 2%', rateType: 'gold', threshold: '₹30,000 single / ₹1,00,000 annual aggregate' },
            { nature: 'Contractor – Company / Others', newSec: '393(1) Sl.6(i).D(b)', code: '1024', oldSec: '194C', rate: '1% / 2%', rateType: 'gold', threshold: '₹30,000 single / ₹1,00,000 annual aggregate' },
            { nature: 'Technical Services / Call Centre / Film Royalty', newSec: '393(1) Sl.6(iii).D(a)', code: '1026', oldSec: '194J(a)', rate: '2%', rateType: 'gold', threshold: '₹50,000' },
            { nature: 'Professional Services Fees', newSec: '393(1) Sl.6(iii).D(b)', code: '1027', oldSec: '194J(b)', rate: '10%', rateType: 'gold', threshold: '₹50,000' },
            { nature: 'Director Remuneration / Fees / Commission', newSec: '393(1) Sl.6(iii).D(b)', code: '1028', oldSec: '194J(b)', rate: '10%', rateType: 'gold', threshold: 'No threshold' },
            { nature: 'Dividends (incl. Preference Shares)', newSec: '393(1) Sl.7', code: '1029', oldSec: '194', rate: '10%', rateType: 'gold', threshold: '₹10,000 per Individual' },
            { nature: 'Life Insurance Policy Proceeds', newSec: '393(1) Sl.8(i)', code: '1030', oldSec: '194DA', rate: '2%', rateType: 'gold', threshold: '₹1 Lakh' },
            { nature: 'Purchase of Goods', newSec: '393(1) Sl.8(ii)', code: '1031', oldSec: '194Q', rate: '0.1%', rateType: 'gold', threshold: 'Exceeds ₹50 Lakh per seller (buyer turnover > ₹10 Cr)' },
            { nature: 'Benefit / Perquisite from Business or Profession', newSec: '393(1) Sl.8(iv)', code: '1033/34', oldSec: '194R', rate: '10%', rateType: 'gold', threshold: '₹20,000 per year' },
            { nature: 'E-Commerce – Sales by Participant', newSec: '393(1) Sl.8(v)', code: '1035', oldSec: '194O', rate: '0.1%', rateType: 'gold', threshold: '₹5 Lakh (Individual / HUF); no threshold for others' },
            { nature: 'Virtual Digital Assets (VDA) Transfer', newSec: '393(1) Sl.8(vi)', code: '1037/38', oldSec: '194S/194SP', rate: '1%', rateType: 'purple', threshold: '₹10,000' },
            { nature: 'Lottery / Card Game / Gambling / Betting', newSec: '393(3) Sl.1', code: '1058/59', oldSec: '194B', rate: '30%', rateType: 'red', threshold: '₹10,000 per single transaction' },
            { nature: 'Online Gaming – Net Winnings', newSec: '393(3) Sl.2', code: '1060/61', oldSec: '194BA', rate: '30%', rateType: 'red', threshold: 'No threshold (or ₹10,000 if in kind)' },
            { nature: 'Horse Race Winnings', newSec: '393(3) Sl.3', code: '1062', oldSec: '194BB', rate: '30%', rateType: 'red', threshold: '₹10,000 per single transaction' },
            { nature: 'Lottery Ticket Commission / Prize', newSec: '393(3) Sl.4', code: '1063', oldSec: '194G', rate: '2%', rateType: 'gold', threshold: '₹20,000' },
            { nature: 'Cash Withdrawal – Co-operative Society', newSec: '393(3) Sl.5.D(a)', code: '1064', oldSec: '194N', rate: '2%', rateType: 'gold', threshold: '₹3 Crore' },
            { nature: 'Cash Withdrawal – Others', newSec: '393(3) Sl.5.D(b)', code: '1065', oldSec: '194N', rate: '2%', rateType: 'gold', threshold: '₹1 Crore' },
            { nature: 'NSS Deposits – Withdrawal u/s 80CCA(2)(a)', newSec: '393(3) Sl.6', code: '1066', oldSec: '194EE', rate: '10%', rateType: 'gold', threshold: '₹2,500' },
            { nature: 'Partner – Salary / Interest / Bonus / Commission', newSec: '393(3) Sl.7', code: '1067', oldSec: '194T', rate: '10%', rateType: 'gold', threshold: '₹20,000 per year — All firms & LLPs', isNew: true },
          ]
        },
        {
          heading: 'TCS Rate Chart — Section 394 (IT Act 2025) | replaces Section 206C',
          icon: '🧾',
          isRateTable: true,
          rates: [
            { nature: 'Sale of Alcoholic Liquor for Human Consumption', newSec: '394(1) Sl.1', code: '1068', oldSec: '206C-A', rate: '2%', rateType: 'gold', threshold: 'Revised to 2% w.e.f. 01-Apr-2026 (was 1%)' },
            { nature: 'Sale of Tendu Leaves', newSec: '394(1) Sl.2', code: '1069', oldSec: '206C-I', rate: '2%', rateType: 'gold', threshold: 'Revised to 2% w.e.f. 01-Apr-2026' },
            { nature: 'Sale of Timber – Under Forest Lease', newSec: '394(1) Sl.3', code: '1070', oldSec: '206C-B', rate: '2%', rateType: 'gold', threshold: 'On sale value; revised rate' },
            { nature: 'Sale of Timber – Other than Forest Lease', newSec: '394(1) Sl.3', code: '1071', oldSec: '206C-C', rate: '2%', rateType: 'gold', threshold: 'On sale value; revised rate' },
            { nature: 'Sale of Other Forest Produce (not timber/tendu)', newSec: '394(1) Sl.3', code: '1072', oldSec: '206C-D', rate: '2%', rateType: 'gold', threshold: 'On sale value under forest lease' },
            { nature: 'Sale of Scrap', newSec: '394(1) Sl.4', code: '1073', oldSec: '206C-E', rate: '2%', rateType: 'gold', threshold: 'Revised to 2% w.e.f. 01-Apr-2026 (was 1%)' },
            { nature: 'Sale of Minerals – Coal / Lignite / Iron Ore', newSec: '394(1) Sl.5', code: '1074', oldSec: '206C-J', rate: '2%', rateType: 'gold', threshold: 'Revised to 2% w.e.f. 01-Apr-2026' },
            { nature: 'Sale of Motor Vehicle', newSec: '394(1) Sl.6', code: '1075', oldSec: '206C-L', rate: '1%', rateType: 'gold', threshold: 'Sale consideration exceeds ₹10 Lakh per transaction' },
            { nature: 'LRS Remittance – Education / Medical Treatment', newSec: '394(1) Sl.7.D(a)', code: '1086', oldSec: '206C-T', rate: '2%', rateType: 'gold', threshold: 'Exceeds ₹10 Lakh; exempt if edu loan from fin. institution' },
            { nature: 'LRS Remittance – Other Purposes', newSec: '394(1) Sl.7.D(b)', code: '1087', oldSec: '206C-Q', rate: '20%', rateType: 'red', threshold: 'Exceeds ₹10 Lakh; all other purposes' },
            { nature: 'Overseas Tour Programme Package – PAN furnished', newSec: '394(1) Sl.8.D(a)', code: '1088', oldSec: '206C-O', rate: '2%', rateType: 'gold', threshold: 'Flat 2% w.e.f. 01-Apr-2026 (was 5%/20%); no threshold' },
            { nature: 'Overseas Tour Programme Package – No PAN', newSec: '394(1) Sl.8.D(b)', code: '1089', oldSec: '206C-O', rate: '2%', rateType: 'gold', threshold: 'Higher rate applies if no PAN per Sec. 397(2)' },
            { nature: 'Parking Lot / Toll / Dam / Canal – Contract / Lease', newSec: '394(1) Sl.9', code: '1090', oldSec: '206C-F', rate: '2%', rateType: 'gold', threshold: 'Business use of parking lots, toll plazas' },
            { nature: 'Mining & Quarrying – Contract / Lease', newSec: '394(1) Sl.10', code: '1091', oldSec: '206C-G', rate: '2%', rateType: 'gold', threshold: 'Excluding coal, lignite, iron ore (covered by 1074)' },
            { nature: 'Sale of Goods > ₹50 Lakh by Seller', newSec: 'N/A under 2025 Act', code: 'N/A', oldSec: '206C(1H)', rate: 'Abolished', rateType: 'na', threshold: 'Discontinued — Only Sec. 393 (194Q equivalent) applies now', isDiscontinued: true },
          ]
        },
        {
          heading: 'Critical Compliance Notes',
          icon: '⚠️',
          isNotes: true,
          notes: [
            { title: 'No PAN — Higher Rate (Sec. 397(2) / Old 206AA & 206CC)', body: 'TDS: Deduct at 20% or applicable rate (whichever is higher). Exception: For Sec. 393(1) Sl. 8(ii)/(v) (goods/e-commerce) — rate is 5%. TCS: Collect at higher of twice the rate, 5%, or 20%.' },
            { title: 'New TRACES Forms — Mandatory from Q1 TY 2026-27', body: 'TDS returns: Form 138 (salary, replaces 24Q), Form 139 (non-salary, replaces 26Q). TCS returns: Form 143 (replaces 27EQ). Certificates: Form 130 (TDS / replaces Form 16) & Form 133 (TCS / replaces Form 27D).' },
            { title: 'Section 194T — Partners (New for TY 2026-27)', body: 'Salary, interest, bonus, commission to partners now attracts TDS @ 10% if aggregate exceeds ₹20,000 p.a. Mandatory for all partnership firms and LLPs from 01 April 2026.' },
            { title: 'TCS Rate Revisions — Finance Act 2026', body: 'Key increases: Alcoholic liquor, tendu leaves, scrap, coal/minerals — all revised from 1% to 2%. Overseas tour packages simplified to flat 2% (removing the earlier 5%/20% two-tier structure). Buyer exemption available for manufacturing purposes.' },
            { title: 'Interest & Penalty for Non-Compliance', body: 'Non-deduction: interest @ 1% p.m. from deductible date. Late deposit: 1.5% p.m. from deduction date. Expense disallowance u/s 40(a)(ia). Penalty u/s 271CA for TCS default equals amount not collected.' },
            { title: 'Exemption for Manufacturing Buyers (TCS Sec. 394)', body: 'No TCS on Sl. Nos. 1–5 of Sec. 394(1) (liquor, tendu, timber, scrap, minerals) if buyer is a resident and furnishes declaration that goods are for manufacturing / processing / power generation (not for trading).' },
          ]
        }
      ],
      // Keep a `dates` array as fallback (used by the generic renderer) — we override rendering below
      dates: []
    },
    {
      id: 'gst',
      label: 'GST',
      icon: '🧾',
      color: '#1a3a6b',
      description: 'Monthly and quarterly GST return filing deadlines.',
      dates: [
        { task: 'GSTR-1 – Monthly (Outward Supply Details)', due: '11th of the following month', note: 'For taxpayers with aggregate turnover > ₹5 Cr' },
        { task: 'GSTR-1 – Quarterly (QRMP Scheme)', due: '13th of the month following the quarter', note: 'For taxpayers with aggregate turnover ≤ ₹5 Cr' },
        { task: 'IFF (Invoice Furnishing Facility – Month 1 & 2 of Quarter)', due: '13th of following month', note: 'Optional for QRMP taxpayers' },
        { task: 'GSTR-3B – Monthly', due: '20th of the following month', note: 'For taxpayers with aggregate turnover > ₹5 Cr' },
        { task: 'GSTR-3B – Quarterly (QRMP Scheme)', due: '22nd / 24th of the month following the quarter', note: 'Based on State category (X or Y)' },
        { task: 'GSTR-9 – Annual Return (Regular Taxpayer)', due: '31st December (following FY)', note: 'Mandatory if turnover > ₹2 Cr; optional below' },
        { task: 'GSTR-9C – Reconciliation Statement', due: '31st December (following FY)', note: 'Mandatory if turnover > ₹5 Cr' },
        { task: 'GSTR-4 – Annual Return (Composition Taxpayer)', due: '30th April (following FY)', note: '' },
        { task: 'CMP-08 – Quarterly Statement (Composition Taxpayer)', due: '18th of the month following the quarter', note: '' },
        { task: 'GSTR-5 – Non-Resident Taxable Person', due: '13th of the following month', note: '' },
        { task: 'GSTR-6 – Input Service Distributor', due: '13th of the following month', note: '' },
        { task: 'GSTR-7 – TDS Deductors under GST', due: '10th of the following month', note: '' },
        { task: 'GSTR-8 – E-Commerce Operators (TCS)', due: '10th of the following month', note: '' },
      ]
    },
    {
      id: 'roc',
      label: 'ROC',
      icon: '🏛️',
      color: '#4a1a6b',
      description: 'Registrar of Companies filings for companies and LLPs.',
      dates: [
        { task: 'AOC-4 – Filing of Financial Statements', due: '30 days from AGM date', note: 'i.e., by 29th October (for Sept 30 AGM)' },
        { task: 'AOC-4 CFS – Consolidated Financial Statements', due: '30 days from AGM date', note: 'For holding companies' },
        { task: 'MGT-7 / MGT-7A – Annual Return', due: '60 days from AGM date', note: 'i.e., by 28th November (for Sept 30 AGM)' },
        { task: 'ADT-1 – Appointment of Auditor', due: '15 days from AGM', note: 'For new/re-appointment of statutory auditor' },
        { task: 'DIR-3 KYC – Director KYC', due: '30th September (every year)', note: 'Annual KYC for all DIN holders' },
        { task: 'DPT-3 – Return of Deposits', due: '30th June', note: 'For companies accepting/outstanding deposits' },
        { task: 'MSME Form I – Outstanding Payments', due: '30th April & 31st October', note: 'Half-yearly; for companies with MSME dues > 45 days' },
        { task: 'BEN-2 – Significant Beneficial Owner Declaration', due: '30 days from receipt of BEN-1', note: '' },
        { task: 'LLP Form 8 – Statement of Account & Solvency', due: '30th October', note: 'For LLPs; covers financial year ending 31st March' },
        { task: 'LLP Form 11 – Annual Return', due: '30th May', note: 'For all registered LLPs' },
        { task: 'PAS-6 – Reconciliation of Share Capital Audit', due: '60 days from end of each half-year', note: 'For unlisted public companies' },
        { task: 'MGT-14 – Filing of Board / Special Resolutions', due: '30 days from passing the resolution', note: 'For specified resolutions under Companies Act' },
      ]
    },
    {
      id: 'others',
      label: 'Others',
      icon: '📌',
      color: '#5a3010',
      description: 'Other important statutory compliance dates including PF, ESI, and more.',
      dates: [
        { task: 'PF Contribution Deposit (Employer + Employee)', due: '15th of the following month', note: 'Via EPFO portal (ECR filing)' },
        { task: 'ESI Contribution Deposit', due: '15th of the following month', note: 'Applicable where ESI Act is applicable' },
        { task: 'PT (Professional Tax) – Maharashtra / Karnataka', due: 'Varies by state (monthly/quarterly)', note: 'Check state-specific due dates' },
        { task: 'FEMA / RBI – FLA Return (Foreign Liabilities & Assets)', due: '15th July', note: 'For companies with FDI/ODI' },
        { task: 'FEMA – FC-GPR (Foreign Currency – Gross Provisional Return)', due: '30 days from allotment of shares', note: '' },
        { task: 'FEMA – FC-TRS (Transfer of Shares)', due: '60 days from receipt/payment', note: '' },
        { task: 'SEBI – Quarterly Compliance Reports (Listed Entities)', due: 'Within 21 days of quarter end', note: 'As per LODR Regulations' },
        { task: 'Income Tax – Form 15CA/CB (Foreign Remittances)', due: 'Before remittance', note: '' },
        { task: 'Equalisation Levy – Return (Form 1)', due: '30th June', note: 'Annual return for digital service providers' },
        { task: 'STT / CTT Return', due: '30th June', note: 'For stock brokers / commodity brokers' },
        { task: 'Shops & Establishment Act Renewal', due: 'Varies by state', note: 'Annual renewal of registration' },
      ]
    }
  ];

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0D1B2A] via-[#1a2d42] to-[#0D1B2A]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(0deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px),
                           repeating-linear-gradient(90deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px)`
        }}></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-playfair font-bold text-[#C9A84C] mb-4">
            Knowledge Bank
          </h2>
          <div className="w-24 h-1 bg-[#C9A84C] mx-auto mb-6"></div>
          <p className="text-gray-300 font-dmsans max-w-3xl mx-auto">
            Tools, resources, and reference materials for tax and compliance management.
          </p>
        </div>

        {/* Subsection Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {subsections.map((subsec) => {
            const Icon = subsec.icon;
            return (
              <button
                key={subsec.id}
                onClick={() => { navigateSubsection(subsec.id); }}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-dmsans font-semibold transition-all duration-300 ${
                  activeSubsection === subsec.id
                    ? 'bg-[#C9A84C] text-[#0D1B2A] shadow-lg'
                    : 'bg-[#1a2d42] bg-opacity-50 text-gray-300 hover:bg-[#C9A84C] hover:text-[#0D1B2A] border border-[#C9A84C] border-opacity-20'
                }`}
              >
                <Icon size={20} />
                {subsec.label}
              </button>
            );
          })}
        </div>

        {/* Calculators */}
        {activeSubsection === 'calculators' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* GST Calculator */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4">GST Calculator</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">Amount (₹)</label>
                  <input
                    type="number"
                    value={gstAmount}
                    onChange={(e) => setGstAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                    placeholder="Enter amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">GST Rate (%)</label>
                  <select
                    value={gstRate}
                    onChange={(e) => setGstRate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                  >
                    <option value="0">0%</option>
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="28">28%</option>
                  </select>
                </div>
                <button
                  onClick={calculateGST}
                  className="w-full bg-[#C9A84C] text-[#0D1B2A] py-2 rounded font-dmsans font-semibold hover:bg-[#b8973d] transition-colors"
                >
                  Calculate
                </button>
                {gstResult && (
                  <div className="mt-4 p-4 bg-[#FAF8F3] rounded text-sm font-dmsans">
                    <p>Base Amount: ₹{gstResult.amount.toFixed(2)}</p>
                    <p>GST Amount: ₹{gstResult.gst.toFixed(2)}</p>
                    <p className="font-bold text-[#0D1B2A]">Total: ₹{gstResult.total.toFixed(2)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Income Tax Calculator */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4">Income Tax Calculator</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">Annual Income (₹)</label>
                  <input
                    type="number"
                    value={income}
                    onChange={(e) => setIncome(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                    placeholder="Enter income"
                  />
                </div>
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">Tax Regime</label>
                  <select
                    value={taxRegime}
                    onChange={(e) => setTaxRegime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                  >
                    <option value="new">New Regime (FY 2025-26)</option>
                    <option value="old">Old Regime (FY 2025-26)</option>
                  </select>
                </div>
                <button
                  onClick={calculateIncomeTax}
                  className="w-full bg-[#C9A84C] text-[#0D1B2A] py-2 rounded font-dmsans font-semibold hover:bg-[#b8973d] transition-colors"
                >
                  Calculate
                </button>
                {taxResult && (
                  <div className="mt-4 p-4 bg-[#FAF8F3] rounded text-sm font-dmsans">
                    <p>Tax Payable: ₹{taxResult.tax.toFixed(2)}</p>
                    <p className="font-bold text-[#0D1B2A]">Net Income: ₹{taxResult.netIncome.toFixed(2)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* EMI Calculator */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4">EMI Calculator</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">Principal (₹)</label>
                  <input
                    type="number"
                    value={principal}
                    onChange={(e) => setPrincipal(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                    placeholder="Loan amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">Rate (% p.a.)</label>
                  <input
                    type="number"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                    placeholder="Interest rate"
                  />
                </div>
                <div>
                  <label className="block text-sm font-dmsans text-gray-700 mb-1">Tenure (years)</label>
                  <input
                    type="number"
                    value={tenure}
                    onChange={(e) => setTenure(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                    placeholder="Loan period"
                  />
                </div>
                <button
                  onClick={calculateEMI}
                  className="w-full bg-[#C9A84C] text-[#0D1B2A] py-2 rounded font-dmsans font-semibold hover:bg-[#b8973d] transition-colors"
                >
                  Calculate
                </button>
                {emiResult && (
                  <div className="mt-4 p-4 bg-[#FAF8F3] rounded text-sm font-dmsans">
                    <p className="font-bold text-[#0D1B2A]">Monthly EMI: ₹{emiResult.emi.toFixed(2)}</p>
                    <p>Total Amount: ₹{emiResult.totalAmount.toFixed(2)}</p>
                    <p>Total Interest: ₹{emiResult.totalInterest.toFixed(2)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Compliance Due Dates */}
        {activeSubsection === 'compliance' && !activeComplianceCategory && (
          <div>
            <p className="text-center text-gray-300 font-dmsans mb-10 text-lg">
              Select a category below to view detailed compliance due dates.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {complianceCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => navigateCategory(cat.id)}
                  className="group text-left bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-t-4 focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                  style={{ borderColor: cat.color }}
                >
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-3xl">{cat.icon}</span>
                      <h3
                        className="text-xl font-playfair font-bold"
                        style={{ color: cat.color }}
                      >
                        {cat.label}
                      </h3>
                    </div>
                    <p className="text-gray-600 font-dmsans text-sm leading-relaxed mb-4">
                      {cat.description}
                    </p>
                    <div className="flex items-center gap-2 font-dmsans text-sm font-semibold" style={{ color: cat.color }}>
                      <span>View Due Dates</span>
                      <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
                    </div>
                    <p className="mt-2 text-xs text-gray-400 font-dmsans opacity-0 group-hover:opacity-100 transition-opacity duration-200 truncate">
                      🔗 #{`knowledge-compliance-${cat.id}`}
                    </p>
                  </div>
                  <div className="h-1 w-0 group-hover:w-full transition-all duration-300" style={{ backgroundColor: cat.color }}></div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Compliance Category Detail Page */}
        {activeSubsection === 'compliance' && activeComplianceCategory && (() => {
          const cat = complianceCategories.find(c => c.id === activeComplianceCategory);
          if (!cat) return null;
          return (
            <div>
              {/* Back Button */}
              <button
                onClick={() => clearCategory()}
                className="flex items-center gap-2 mb-8 text-[#C9A84C] font-dmsans font-semibold hover:underline focus:outline-none transition-all duration-200"
              >
                <span>←</span>
                <span>Back to Categories</span>
              </button>

              {/* Page Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                  <span className="text-4xl">{cat.icon}</span>
                  <div>
                    <h3 className="text-3xl font-playfair font-bold" style={{ color: '#C9A84C' }}>
                      {cat.label} — Compliance Due Dates
                    </h3>
                    <p className="text-gray-300 font-dmsans mt-1">{cat.description}</p>
                  </div>
                </div>

                {/* Shareable link button */}
                <ShareLinkButton categoryId={cat.id} categoryLabel={cat.label} />
              </div>

              {/* Alert banner (TDS structural change notice) */}
              {cat.alert && (
                <div className="mb-6 p-4 rounded-lg border-l-4 bg-amber-50 border-amber-500">
                  <p className="text-xs font-dmsans font-bold text-amber-800 mb-1">⚠️ {cat.alert.title}</p>
                  <p className="text-xs font-dmsans text-amber-700 leading-relaxed">{cat.alert.body}</p>
                </div>
              )}

              {/* Rich multi-section renderer (TDS) */}
              {cat.sections ? (
                <div className="space-y-8">
                  {cat.sections.map((sec, si) => (
                    <div key={si} className="bg-white rounded-xl shadow-xl overflow-hidden">
                      {/* Section header */}
                      <div className="px-6 py-4 text-white font-playfair text-base font-semibold flex items-center gap-2" style={{ backgroundColor: cat.color }}>
                        <span>{sec.icon}</span>
                        <span>{sec.heading}</span>
                      </div>

                      {/* Due dates sub-table */}
                      {sec.dates && (
                        <div className="overflow-x-auto">
                          <table className="w-full font-dmsans text-sm">
                            <thead>
                              <tr className="bg-[#FAF8F3] border-b-2 border-gray-200">
                                <th className="px-5 py-3 text-left text-[#0D1B2A] font-semibold w-8">#</th>
                                <th className="px-5 py-3 text-left text-[#0D1B2A] font-semibold">Compliance Task</th>
                                <th className="px-5 py-3 text-left text-[#0D1B2A] font-semibold whitespace-nowrap">Due Date</th>
                                <th className="px-5 py-3 text-left text-[#0D1B2A] font-semibold">Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sec.dates.map((item, idx) => (
                                <tr key={idx} className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-[#FAF8F3]'} hover:bg-blue-50 transition-colors duration-150`}>
                                  <td className="px-5 py-3 text-gray-400 font-semibold">{idx + 1}</td>
                                  <td className="px-5 py-3 text-gray-800 font-medium leading-snug">{item.task}</td>
                                  <td className="px-5 py-3">
                                    <span className="inline-block px-3 py-1 rounded-full text-white text-xs font-semibold whitespace-nowrap" style={{ backgroundColor: cat.color }}>
                                      {item.due}
                                    </span>
                                  </td>
                                  <td className="px-5 py-3 text-gray-500 text-xs leading-relaxed italic">{item.note || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Rate chart table */}
                      {sec.isRateTable && sec.rates && (
                        <div className="overflow-x-auto">
                          <table className="w-full font-dmsans text-xs">
                            <thead>
                              <tr className="bg-[#FAF8F3] border-b-2 border-gray-200">
                                <th className="px-4 py-3 text-left text-[#0D1B2A] font-semibold" style={{minWidth:'220px'}}>Nature of Payment / Transaction</th>
                                <th className="px-4 py-3 text-left text-[#0D1B2A] font-semibold whitespace-nowrap">New Section (IT Act 2025)</th>
                                <th className="px-4 py-3 text-left text-[#0D1B2A] font-semibold whitespace-nowrap">TRACES Code</th>
                                <th className="px-4 py-3 text-left text-[#0D1B2A] font-semibold whitespace-nowrap">Old Section</th>
                                <th className="px-4 py-3 text-left text-[#0D1B2A] font-semibold">Rate</th>
                                <th className="px-4 py-3 text-left text-[#0D1B2A] font-semibold">Threshold / Key Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sec.rates.map((r, ri) => {
                                const rateBadge = {
                                  gold:   'bg-amber-100 text-amber-800 border border-amber-300',
                                  red:    'bg-red-100 text-red-700 border border-red-300',
                                  blue:   'bg-blue-100 text-blue-800 border border-blue-300',
                                  purple: 'bg-purple-100 text-purple-800 border border-purple-300',
                                  na:     'bg-gray-100 text-gray-500 border border-gray-300',
                                }[r.rateType] || 'bg-amber-100 text-amber-800 border border-amber-300';
                                return (
                                  <tr key={ri} className={`border-b border-gray-100 ${ri % 2 === 0 ? 'bg-white' : 'bg-[#FAF8F3]'} ${r.isDiscontinued ? 'opacity-60' : ''} hover:bg-blue-50 transition-colors duration-150`}>
                                    <td className="px-4 py-2.5 text-gray-800 font-medium leading-snug">
                                      {r.nature}
                                      {r.isNew && <span className="ml-2 inline-block bg-green-100 text-green-700 border border-green-300 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase">New</span>}
                                      {r.isDiscontinued && <span className="ml-2 inline-block bg-red-100 text-red-700 border border-red-300 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase">Abolished</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-blue-700 font-mono font-semibold">{r.newSec}</td>
                                    <td className="px-4 py-2.5 text-blue-800 font-mono font-bold">{r.code}</td>
                                    <td className="px-4 py-2.5 text-gray-400 font-mono">{r.oldSec}</td>
                                    <td className="px-4 py-2.5">
                                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${rateBadge}`}>{r.rate}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-gray-600 leading-snug">{r.threshold}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Compliance notes grid */}
                      {sec.isNotes && sec.notes && (
                        <div className="grid md:grid-cols-2 gap-4 p-6">
                          {sec.notes.map((n, ni) => (
                            <div key={ni} className="border border-gray-200 rounded-lg p-4" style={{ borderTopWidth: '3px', borderTopColor: cat.color }}>
                              <p className="font-dmsans font-bold text-sm mb-2" style={{ color: cat.color }}>{n.title}</p>
                              <p className="font-dmsans text-xs text-gray-600 leading-relaxed">{n.body}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="px-6 py-3 bg-[#FAF8F3] border-t border-gray-200 text-xs text-gray-500 font-dmsans italic">
                        {sec.isRateTable
                          ? '* Rates as per Income Tax Act, 2025 effective 01 April 2026 (Tax Year 2026-27). Old section numbers shown for cross-reference only. Rates subject to CBDT notifications.'
                          : '* Due dates are indicative and may be extended by government notifications. Always verify from official sources.'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              /* Standard due dates table (Income Tax, GST, ROC, Others) */
              <div className="bg-white rounded-xl shadow-xl overflow-hidden">
                <div className="px-6 py-4 text-white font-playfair text-lg font-semibold flex items-center gap-2" style={{ backgroundColor: cat.color }}>
                  <span>{cat.icon}</span>
                  <span>{cat.label} Due Date Schedule</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full font-dmsans text-sm">
                    <thead>
                      <tr className="bg-[#FAF8F3] border-b-2 border-gray-200">
                        <th className="px-6 py-3 text-left text-[#0D1B2A] font-semibold w-8">#</th>
                        <th className="px-6 py-3 text-left text-[#0D1B2A] font-semibold">Compliance Task</th>
                        <th className="px-6 py-3 text-left text-[#0D1B2A] font-semibold whitespace-nowrap">Due Date</th>
                        <th className="px-6 py-3 text-left text-[#0D1B2A] font-semibold">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cat.dates.map((item, index) => (
                        <tr
                          key={index}
                          className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-[#FAF8F3]'} hover:bg-yellow-50 transition-colors duration-150`}
                        >
                          <td className="px-6 py-4 text-gray-400 font-semibold">{index + 1}</td>
                          <td className="px-6 py-4 text-gray-800 font-medium leading-snug">{item.task}</td>
                          <td className="px-6 py-4">
                            <span
                              className="inline-block px-3 py-1 rounded-full text-white text-xs font-semibold whitespace-nowrap"
                              style={{ backgroundColor: cat.color }}
                            >
                              {item.due}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-500 text-xs leading-relaxed italic">
                            {item.note || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-6 py-3 bg-[#FAF8F3] border-t border-gray-200 text-xs text-gray-500 font-dmsans italic">
                  * Due dates are indicative and may be extended by government notifications. Always verify from official sources.
                </div>
              </div>
              )}

              {/* Jump to other categories */}
              <div className="mt-10">
                <p className="text-gray-400 font-dmsans text-sm mb-4 text-center">Jump to another category:</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {complianceCategories.filter(c => c.id !== cat.id).map(c => (
                    <button
                      key={c.id}
                      onClick={() => navigateCategory(c.id)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1a2d42] border border-[#C9A84C] border-opacity-30 text-gray-300 font-dmsans text-sm hover:bg-[#C9A84C] hover:text-[#0D1B2A] transition-all duration-200"
                    >
                      <span>{c.icon}</span>
                      <span>{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Useful Links */}
        {activeSubsection === 'links' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {usefulLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-t-4 border-[#C9A84C] flex items-center gap-4"
              >
                <ExternalLink className="text-[#C9A84C] flex-shrink-0" size={24} />
                <span className="font-dmsans font-semibold text-[#0D1B2A]">{link.name}</span>
              </a>
            ))}
          </div>
        )}

        {/* Key Reference Rates */}
        {activeSubsection === 'rates' && (
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4 border-b-2 border-[#C9A84C] pb-2">
                Income Tax Slabs (FY 2025-26)
              </h3>
              <div className="space-y-4 font-dmsans text-sm">
                <div>
                  <p className="font-semibold text-[#0D1B2A] mb-2">New Regime:</p>
                  <ul className="space-y-1 text-gray-700">
                    <li>₹0 - ₹3,00,000: Nil</li>
                    <li>₹3,00,001 - ₹7,00,000: 5%</li>
                    <li>₹7,00,001 - ₹10,00,000: 10%</li>
                    <li>₹10,00,001 - ₹12,00,000: 15%</li>
                    <li>₹12,00,001 - ₹15,00,000: 20%</li>
                    <li>Above ₹15,00,000: 30%</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-[#0D1B2A] mb-2">Old Regime:</p>
                  <ul className="space-y-1 text-gray-700">
                    <li>₹0 - ₹2,50,000: Nil</li>
                    <li>₹2,50,001 - ₹5,00,000: 5%</li>
                    <li>₹5,00,001 - ₹10,00,000: 20%</li>
                    <li>Above ₹10,00,000: 30%</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4 border-b-2 border-[#C9A84C] pb-2">
                GST Rates Overview
              </h3>
              <ul className="space-y-2 font-dmsans text-sm text-gray-700">
                <li className="flex justify-between">
                  <span>Essential goods (food grains, etc.)</span>
                  <span className="font-semibold">0%</span>
                </li>
                <li className="flex justify-between">
                  <span>Necessities (medicines, etc.)</span>
                  <span className="font-semibold">5%</span>
                </li>
                <li className="flex justify-between">
                  <span>Standard goods</span>
                  <span className="font-semibold">12%</span>
                </li>
                <li className="flex justify-between">
                  <span>Most goods & services</span>
                  <span className="font-semibold">18%</span>
                </li>
                <li className="flex justify-between">
                  <span>Luxury items (cars, etc.)</span>
                  <span className="font-semibold">28%</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4 border-b-2 border-[#C9A84C] pb-2">
                TDS Rate Chart
              </h3>
              <ul className="space-y-2 font-dmsans text-sm text-gray-700">
                <li className="flex justify-between">
                  <span>194A - Interest (Bank/Company)</span>
                  <span className="font-semibold">10%</span>
                </li>
                <li className="flex justify-between">
                  <span>194C - Contractor Payments</span>
                  <span className="font-semibold">1-2%</span>
                </li>
                <li className="flex justify-between">
                  <span>194H - Commission/Brokerage</span>
                  <span className="font-semibold">5%</span>
                </li>
                <li className="flex justify-between">
                  <span>194I - Rent (Plant & Machinery)</span>
                  <span className="font-semibold">2%</span>
                </li>
                <li className="flex justify-between">
                  <span>194I - Rent (Land & Building)</span>
                  <span className="font-semibold">10%</span>
                </li>
                <li className="flex justify-between">
                  <span>194J - Professional/Technical</span>
                  <span className="font-semibold">10%</span>
                </li>
                <li className="flex justify-between">
                  <span>194Q - Purchase of Goods</span>
                  <span className="font-semibold">0.1%</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] mb-4 border-b-2 border-[#C9A84C] pb-2">
                Depreciation Rates (Companies Act)
              </h3>
              <ul className="space-y-2 font-dmsans text-sm text-gray-700">
                <li className="flex justify-between">
                  <span>Buildings</span>
                  <span className="font-semibold">5-10%</span>
                </li>
                <li className="flex justify-between">
                  <span>Plant & Machinery</span>
                  <span className="font-semibold">15%</span>
                </li>
                <li className="flex justify-between">
                  <span>Furniture & Fixtures</span>
                  <span className="font-semibold">10%</span>
                </li>
                <li className="flex justify-between">
                  <span>Computers & Software</span>
                  <span className="font-semibold">40%</span>
                </li>
                <li className="flex justify-between">
                  <span>Motor Vehicles</span>
                  <span className="font-semibold">15-30%</span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default KnowledgeBankSection;
